/* eslint-disable @typescript-eslint/naming-convention */
import React, { Fragment, useEffect, useRef, useState } from "react";

import {
  Autocomplete,
  CircularProgress,
  FormHelperText,
  OutlinedInputProps,
  PaperProps,
  TextField,
  AutocompleteGetTagProps,
  AutocompleteRenderOptionState,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  FilterOptionsState,
} from "@mui/material/useAutocomplete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import withStyles from "@mui/styles/withStyles";
import { AxiosResponse } from "axios";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { FiChevronDown } from "react-icons/fi";
import Button, { Color } from "../Button";
import Chip from "../Chip";
import useStyles, { FormControlCustomized, PaperCustomized, WrapperInfo } from "./styles";
import { getBrandCustom } from "Util/getCustomColor";
import { colors } from "@zapalacorp/tokens";

export interface AutoSuggestProps<T> {
  ref?: React.Ref<unknown> | undefined;
  dataCy?: string;
  label?: string;
  name?: string;
  error?: boolean;
  errorMessage?: string;
  infoMessage?: string;
  disableClearable?: boolean;
  clearOptionOnEscape?: boolean;
  rightIcon?: JSX.Element;
  clearOptionsOnEscape?: boolean;
  placeholder?: string;
  onBlur?: (value: any) => void;
  onChange?: (value: any, reason?: AutocompleteChangeReason) => void;
  onChangeInput?: (value: string, reason: AutocompleteInputChangeReason) => void;
  value?: any;
  defaultValue?: any;
  multiple?: boolean;
  freeSolo?: boolean;
  removeShadowBox?: boolean;
  options?: T[];
  inputValue?: string;
  disabled?: boolean;
  onSearch?: (value: string) => Promise<AxiosResponse<any>>;
  getOptionKey?: ((option: T) => string | number) | undefined;
  getOptionLabel?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  // DEPRECATED: Mantido para compatibilidade, mas será removido
  getOptionSelected?: (option: T, value: T) => boolean;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  noOptionsText?: React.ReactNode;
  closeIcon?: React.ReactNode;
  autoSelect?: boolean;
  autoHighlight?: boolean;
  autoComplete?: boolean;
  tagDataCy?: string;
  bordercolor?: string;
  required?: boolean;
  renderOption?: (props: any, option: T, state: AutocompleteRenderOptionState) => React.ReactNode;
  InputProps?: Partial<OutlinedInputProps>;
  fillInputValueOnChange?:
    | ((value: string, reason: AutocompleteInputChangeReason) => boolean)
    | boolean;
  addOptionTitle?: string;
  onAddOption?: () => void;
  limitTags?: number;
  onFocus?: React.FocusEventHandler<HTMLDivElement> | undefined;
  groupBy?: (option: T) => string;
  readOnly?: boolean;
  removeArrow?: boolean;
  disableCloseOnSelect?: boolean;
  ignoreValue?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  selectIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  disablePortal?: boolean;
}

const TextFieldCustomized = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "bordercolor" && prop !== "error" && prop !== "disabled" && prop !== "startIcon",
})<{
  bordercolor?: string;
  error?: boolean;
  disabled?: boolean;
  startIcon?: React.ReactNode;
}>(({ bordercolor = "#C4C4C4", error, disabled }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F9FAFB" : "#FFFFFF",
    minHeight: "40px",
    borderRadius: "8px",
    fontFamily: "Manrope",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "24px",
    color: "#667085",
    paddingTop: "0px !important",
    paddingBottom: "0px !important",
    paddingLeft: "16px !important",
    "& fieldset": {
      borderColor: error ? bordercolor : "#C4C4C4",
    },
    "&:hover fieldset": {
      borderColor: !disabled ? bordercolor : "#C4C4C4",
    },
    "&.Mui-focused fieldset": {
      borderColor: bordercolor,
    },
  },
}));

const SelectIcon = styled(FiChevronDown)(() => ({
  height: "20px",
  width: "20px",
}));

const AutoSuggest = <T extends unknown>(props: AutoSuggestProps<T>) => {
  const {
    label,
    name,
    value = "",
    inputValue,
    errorMessage,
    infoMessage = "",
    disableClearable,
    onChangeInput,
    error,
    placeholder,
    onChange,
    freeSolo,
    removeShadowBox = true,
    multiple,
    onSearch,
    getOptionSelected,
    isOptionEqualToValue,
    getOptionLabel,
    getOptionKey,
    options,
    disabled,
    filterOptions,
    rightIcon,
    closeIcon,
    clearOptionsOnEscape,
    dataCy,
    tagDataCy,
    fillInputValueOnChange = true,
    required,
    addOptionTitle,
    onAddOption,
    renderOption,
    InputProps,
    readOnly = false,
    removeArrow = false,
    disableCloseOnSelect = false,
    ignoreValue = false,
    defaultValue,
    onClose,
    onOpen,
    autoComplete,
    selectIcon,
    startIcon,
    disablePortal = false,
    ...rest
  } = props;

  const classes = useStyles({ disabled });

  const [_options, setOptions] = useState<T[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [_inputValue, setInputValue] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setInputValue(inputValue || "");
  }, [inputValue]);

  useEffect(() => {
    setOptions(options || []);
  }, [options]);

  useEffect(() => {
    if (inputValue !== undefined) return;

    const isEmpty = !value || (Array.isArray(value) && value.length === 0);

    if (isEmpty) {
      setInputValue("");
    } else if (!multiple && getOptionLabel && typeof value === "object") {
      setInputValue(getOptionLabel(value as T) || "");
    }
  }, [value, multiple, inputValue]);

  const requestNewSuggests = async (search: string, reason: AutocompleteInputChangeReason) => {
    const hasOptionComparison = isOptionEqualToValue || getOptionSelected;
    if (onSearch && getOptionLabel && hasOptionComparison && search && reason === "input") {
      try {
        if (!isMountedRef.current) return;
        setLoading(true);
        const results = await onSearch(search);

        if (!isMountedRef.current) return;
        if (results?.data?.rows) setOptions(results.data.rows);
        else if (results?.data?.data) setOptions(results.data.data);
        else if (results?.data) setOptions(results.data);
        setLoading(false);
      } catch (error) {
        if (!isMountedRef.current) return;
        setLoading(false);
      }
    }
  };

  const onInputChange = (value: string, reason: AutocompleteInputChangeReason) => {
    const fill =
      fillInputValueOnChange instanceof Function
        ? fillInputValueOnChange(value, reason)
        : fillInputValueOnChange;

    if (onChangeInput) {
      onChangeInput(value, reason);
    } else if (fill) {
      setInputValue(value);
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => requestNewSuggests(value, reason), 500);
  };

  const renderTags = (array: T[], getTagProps: AutocompleteGetTagProps) => {
    return array
      .map((value, index) => {
        if (getOptionLabel) {
          // getOptionLabel agora aceita tanto T quanto string
          const label = getOptionLabel(value);
          return (
            <Chip
              {...getTagProps({ index })}
              data-cy={`${tagDataCy}-${label}`}
              label={<span>{label}</span>}
            />
          );
        }
        return null;
      })
      .filter(Boolean);
  };

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  const renderInfoLabel = () => {
      if (infoMessage?.trim().length) {
        return (
          <Tooltip title={infoMessage} placement="top">
            <WrapperInfo>
              <InfoOutlinedIcon className={classes.extraSmallIcon} />
            </WrapperInfo>
          </Tooltip>
        );
      }

      return null;
    };

  const renderLabel = () => {
    if (label) {
      return (
        <span className={classes.label}>
          {label}
          {renderRequiredLabel()}
          {renderInfoLabel()}
        </span>
      );
    }

    return null;
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText className={classes.formHelper} data-cy="errors">
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }
  };

  const renderPaper = (props: PaperProps) => {
    return (
      <PaperCustomized
        elevation={8}
        {...props}
        className={freeSolo && removeShadowBox ? "no-shadow" : undefined}
      >
        {props.children}
        {addOptionTitle && (
          <div className={classes.addButton}>
            <Button
              onMouseDown={onAddOption}
              startIcon={<AiOutlinePlusCircle color={colors.brandBlue500} />}
              customColor={Color.SEND}
            >
              {addOptionTitle}
            </Button>
          </div>
        )}
      </PaperCustomized>
    );
  };

  return (
    <FormControlCustomized
      disabled={disabled}
      bordercolor={error ? "#D92D20" : getBrandCustom("primaryColor", colors.brandBlue500)}
    >
      {renderLabel()}
      <Autocomplete
        data-cy={dataCy}
        {...rest}
        open={open}
        onOpen={() => {
          if (!readOnly) {
            setOpen(true);
            if (onOpen) onOpen();
          }
        }}
        getOptionKey={
          getOptionKey &&
          function (option: T | string) {
            return getOptionKey?.(option as T);
          }
        }
        disableClearable={disableClearable}
        disabled={disabled}
        multiple={multiple}
        freeSolo={freeSolo}
        fullWidth
        options={_options}
        disablePortal={disablePortal}
        value={ignoreValue ? undefined : value || null}
        filterOptions={filterOptions}
        inputValue={_inputValue.replace("  ", " ").trimStart()}
        isOptionEqualToValue={isOptionEqualToValue || getOptionSelected}
        getOptionLabel={getOptionLabel as any}
        onClose={() => {
          if (clearOptionsOnEscape) setOptions([]);
          setOpen(false);
          if (onClose) onClose();
        }}
        clearOnEscape={false}
        clearOnBlur={false}
        clearIcon={closeIcon}
        onInputChange={(event, value, reason) => {
          if (multiple && reason === "reset") {
            setInputValue("");
            return;
          }
          if (reason !== "input") return;
          if (!readOnly) onInputChange(value, reason);
        }}
        onChange={(event, value, reason) => {
          if (onChange && !readOnly) onChange(value, reason);
        }}
        defaultValue={defaultValue}
        renderTags={renderTags}
        popupIcon={selectIcon || <SelectIcon />}
        PaperComponent={renderPaper}
        renderOption={
          renderOption &&
          ((props, option, state) => {
            return renderOption?.(props, option, state);
          })
        }
        renderInput={(params) => (
          <TextFieldCustomized
            data-cy={dataCy}
            {...params}
            name={name}
            InputProps={{
              ...params.InputProps,
              startAdornment: startIcon ? (
                <InputAdornment position="start">{startIcon}</InputAdornment>
              ) : (
                params.InputProps?.startAdornment
              ),
              endAdornment: (
                <Fragment>
                  {loading ? (
                    <CircularProgress className={classes.circularProgress} size={20} />
                  ) : null}
                  {!removeArrow && params.InputProps.endAdornment}
                  {rightIcon}
                </Fragment>
              ),
              ...InputProps,
            }}
            startIcon={startIcon}
            disabled={disabled}
            error={error}
            bordercolor={error ? "#D92D20" : getBrandCustom("primaryColor", colors.brandBlue500)}
            variant="outlined"
            placeholder={placeholder}
          />
        )}
        autoComplete={autoComplete}
        disableCloseOnSelect={disableCloseOnSelect}
      />
      {renderError()}
    </FormControlCustomized>
  );
};

export default AutoSuggest;
